/**
 * Configuration du bureau open space — "The Hive"
 * Grille 7×7 (indices 0–6)
 *
 * ┌─────────────────────────────┐
 * │  0   1   2   3   4   5   6  │
 * │  ·   ·   ·   ·   ·   ·   · │ 0
 * │  ·  [ALEX ]  ·   ·  [BOB  ] │ 1
 * │  ·  [desk ]  ·   ·  [desk ] │ 2
 * │  ·  chair  ·   ·  chair   · │ 3
 * │  ·  PL  ·  ☕  ·   ·  PL   │ 4
 * │  ·   ·  🛋  TBL  🛋  ·   · │ 5
 * │  ·  [CARL ]  ·   ·  PRT   │ 6
 * └─────────────────────────────┘
 */

/**
 * Matrice de walkabilité 7x7
 * true = le personnage peut marcher sur cette case
 * false = la case est bloquée par un meuble
 * 
 * [y][x] - y = ligne, x = colonne
 */
export const WALKABLE_MATRIX = [
  // x=0   1     2     3     4     5     6
  [true,  false,  false,  true,  true,  true,  true ],  // y=0
  [false,  true,  true, true,  true,  false, true ],  // y=1 (tables)
  [false,  false,  false, true,  true,  false, false ],  // y=2 (tables)
  [true,  false,  true, true,  true,  false, false ],  // y=3 (tables + chaises)
  [true,  true,  true,  false, true,  true,  true ],  // y=4 (coffee machine)
  [true,  false,  false, false, false, false,  true ],  // y=5 (sofas + coffee table)
  [false,  false,  false, false, false, false,  true],  // y=6 (Carl desk + sofas)
];

// Orientations disponibles
const ORIENTATIONS = {
  NW: 'north-west',
  NE: 'north-east',
  SE: 'south-east',
  SW: 'south-west'
};

/**
 * Configuration complète du mobilier
 * Format: { type, x, y, orientation, options? }
 * Tous les meubles sont dans la grille 0-6
 */
export const OFFICE_FURNITURE_LAYOUT = [

  // ═══════════════════════════════════════════
  //  POSTE DE TRAVAIL — Alex (CEO · zone rouge)
  // ═══════════════════════════════════════════
  {
    group: 'workstation_alex',
    items: [
      { type: 'table_brown_2x2', x: 1.4, y: 3, orientation: ORIENTATIONS.SE, options: { offSetY: -1, customScale: 1.7, zIndex: 0 } },
      { type: 'chair_black',     x: 0.7, y: 3, orientation: ORIENTATIONS.NW, options: { offsetY: -5, offsetX: 2, customScale: 0.6} },
      { type: 'coffee_mug', x: 1.4, y: 3, orientation: ORIENTATIONS.SE, options: { offsetY: -34, offsetX: 22, customScale: 0.2, zOffset: 5 } },
      { type: 'fax', x: 1.4, y: 3, orientation: ORIENTATIONS.SW, options: { offsetY: -58, customScale: 0.35, zOffset: 5 } },
      { type: 'pc_02', x: 1.4, y: 3, orientation: ORIENTATIONS.SE, options : { offsetX: 4, offsetY: -30, customScale: 0.4, zOffset: 5 } }
    ]
  },

  // ═══════════════════════════════════════════
  //  POSTE DE TRAVAIL — Bob (Lead Dev · zone bleue)
  //  Déplacé de x:7 à x:5 pour rester dans 0-6
  // ═══════════════════════════════════════════
  {
    group: 'workstation_bob',
    items: [
      { type: 'table_brown_2x2', x: 6.7, y: 3, orientation: ORIENTATIONS.NW, options: { offsetY: -5, customScale: 1.7 } },
      { type: 'chair_black',     x: 6.7, y: 2.5, orientation: ORIENTATIONS.SW, options: { offsetY: -8, customScale: 0.6 } },
      { type: 'pc_01',   x: 6.7, y: 3, orientation: ORIENTATIONS.NE, options: { offsetX: -6, offsetY: -26, customScale: 0.7, zOffset: 5 } },
      { type: 'coffee_mug', x: 6.7, y: 3, orientation: ORIENTATIONS.SE, options: { offsetX: -8,  offsetY: -50, customScale: 0.2, zOffset: 5 } },
    ]
  },

  // ═══════════════════════════════════════════
  //  POSTE DE TRAVAIL — Carl (COO · zone verte)
  //  Déplacé de y:6.7 à y:5 pour rester dans 0-6
  // ═══════════════════════════════════════════
  {
    group: 'workstation_carl',
    items: [
      { type: 'table_brown_2x2', x: 1.5, y: 6.7, orientation: ORIENTATIONS.NE, options: { offsetY: -5, customScale: 1.5 } },
      { type: 'chair_black_02',  x: 0.8, y: 6, orientation: ORIENTATIONS.NW, options: { offsetY: -8, customScale: 0.5 } },
      { type: 'pc_02',   x: 1.5, y: 6.7, orientation: ORIENTATIONS.SE, options: { offsetX: 20, offsetY: -38, customScale: 0.4, zOffset: 5 } },
      { type: 'notepad',    x: 1.5, y: 6.7, orientation: ORIENTATIONS.SE, options: { offsetX: 5,  offsetY: -26, customScale: 0.2, zOffset: 5 } },
      { type: 'organizer',  x: 1.5, y: 6.7, orientation: ORIENTATIONS.NE, options: { offsetX: -18, offsetY: -32, customScale: 0.2, zOffset: 5 } },
    ]
  },

  // ═══════════════════════════════════════════
  //  ESPACE DÉTENTE
  // ═══════════════════════════════════════════
  {
    group: 'lounge_area',
    items: [
      { type: 'couch_brown', x: 3, y: 6, orientation: ORIENTATIONS.NW },
      { type: 'couch_brown', x: 5, y: 6, orientation: ORIENTATIONS.SE },
      { type: 'table_coffee_brown', x: 4, y: 6, orientation: ORIENTATIONS.SE },
      { type: 'coffee_mug', x: 4, y: 6, orientation: ORIENTATIONS.SE, options: { offsetX: -8, offsetY: -8, customScale: 0.18, zOffset: 3 } }
    ]
  },

  // ═══════════════════════════════════════════
  //  COIN CAFÉ
  // ═══════════════════════════════════════════
  {
    group: 'coffee_corner',
    items: [
      { type: 'table_brown_2x1', x: 4, y: 5, orientation: ORIENTATIONS.SW, options: { offsetY: -3 } },
      { type: 'coffee_machine',  x: 4, y: 5, orientation: ORIENTATIONS.SW, options: { offsetY: -22, customScale: 0.45 } }
    ]
  },

  // ═══════════════════════════════════════════
  //  ZONE ÉQUIPEMENT
  // ═══════════════════════════════════════════
  {
    group: 'equipment_area',
    items: [
      { type: 'cabinet_01', x: 1, y: 0, orientation: ORIENTATIONS.SW, options: { offsetY: 5, customScale: 0.6 } }
    ]
  },

  // ═══════════════════════════════════════════
  //  DÉCORATION & PLANTES (toutes dans 0-6)
  // ═══════════════════════════════════════════
  {
    group: 'plants',
    items: [
      { type: 'plant_03', x: 2.2, y: 0.4, orientation: ORIENTATIONS.SE }
    ]
  },

  {
    group: 'wall_decor',
    items: [
      { type: 'whiteboard', x: 0, y: 4, orientation: ORIENTATIONS.SE, options: { offsetY: -40 } }
    ]
  },

  // ═══════════════════════════════════════════
  //  PORTE D'ENTRÉE
  // ═══════════════════════════════════════════
  {
    group: 'entrance',
    items: [
      { type: 'door', x: 2, y: 0, orientation: ORIENTATIONS.SW, options: { offsetY: 20, offsetX: 40, customScale: 0.8 } }
    ]
  }
];

export default OFFICE_FURNITURE_LAYOUT;
